/* eslint-disable tailwindcss/no-arbitrary-value */
import { useBreakpoint } from 'car-insurance/hooks/useBreakpoint';
import classNames from 'classnames';
import { MaterialIcon } from 'components/modulab/MaterialIcon';

export type PaginatorProps = {
  count: number;
  current: number;
  onChange: (index: number) => void;
};

type PaginationLayout = {
  visible: number[];
  leftDots: boolean;
  rightDots: boolean;
};

const SIDE_COUNT = 2;

export const Paginator: React.FC<PaginatorProps> = ({
  count,
  current,
  onChange,
}) => {
  const isMobile = useBreakpoint() === 'sm';

  const goTo = (index: number) => {
    if (index < 1 || index > count || index === current) return;
    onChange(index);
  };

  const getMobileButtons = (
    current: number,
    count: number,
  ): PaginationLayout => {
    if (count <= 4) {
      return {
        visible: Array.from({ length: count }, (_, i) => i + 1),
        leftDots: false,
        rightDots: false,
      };
    }

    if (current <= 3)
      return { visible: [1, 2, 3], leftDots: false, rightDots: true };

    if (current >= count - 2)
      return {
        visible: [count - 2, count - 1, count],
        leftDots: true,
        rightDots: false,
      };

    return { visible: [current], leftDots: true, rightDots: true };
  };

  const getDesktopButtons = (): PaginationLayout => {
    let start = current - SIDE_COUNT;
    let end = current + SIDE_COUNT;

    if (start < 2) {
      end += 2 - start;
      start = 2;
    }

    if (end > count - 1) {
      start -= end - (count - 1);
      end = count - 1;
    }

    start = Math.max(start, 2);
    end = Math.min(end, count - 1);

    const visible = [];
    for (let i = start; i <= end; i++) visible.push(i);

    return {
      visible,
      leftDots: start > 2,
      rightDots: end < count - 1,
    };
  };

  const {
    visible: visibleButtons,
    leftDots,
    rightDots,
  } = isMobile ? getMobileButtons(current, count) : getDesktopButtons();

  const buttonBaseClasses = classNames(
    'mhg-alpha-button-1 flex size-[--button-size-regular] cursor-pointer select-none items-center justify-center rounded-radius/button',
  );
  const buttonSelectedClasses = classNames(
    'bg-fill/primary-light text-fill/primary-dark',
  );

  return (
    <div className="flex items-center justify-center gap-spacing/gap/soft-12px">
      <button
        type="button"
        className={buttonBaseClasses}
        disabled={current === 1}
        onClick={() => goTo(current - 1)}
      >
        <MaterialIcon
          icon="chevron_left"
          px={24}
          className={
            current === 1 ? 'text-buttons/disable' : 'text-text/primary'
          }
        />
      </button>

      {visibleButtons[0] !== 1 && (
        <button
          type="button"
          onClick={() => goTo(1)}
          className={classNames(
            buttonBaseClasses,
            current === 1 ? buttonSelectedClasses : 'text-buttons/neutral-dark',
          )}
        >
          1
        </button>
      )}

      {leftDots && <div className={buttonBaseClasses}>…</div>}

      {visibleButtons.map((index) => (
        <button
          key={index}
          type="button"
          onClick={() => goTo(index)}
          className={classNames(
            buttonBaseClasses,
            index === current
              ? buttonSelectedClasses
              : 'text-buttons/neutral-dark',
          )}
        >
          {index}
        </button>
      ))}

      {rightDots && <div className={buttonBaseClasses}>…</div>}

      {visibleButtons[visibleButtons.length - 1] !== count && (
        <button
          type="button"
          onClick={() => goTo(count)}
          className={classNames(
            buttonBaseClasses,
            current === count
              ? buttonSelectedClasses
              : 'text-buttons/neutral-dark',
          )}
        >
          {count}
        </button>
      )}

      <button
        type="button"
        className={buttonBaseClasses}
        disabled={current === count}
        onClick={() => goTo(current + 1)}
      >
        <MaterialIcon
          icon="chevron_right"
          px={24}
          className={
            current === count ? 'text-buttons/disable' : 'text-text/primary'
          }
        />
      </button>
    </div>
  );
};
